<template>
  <div class="enhancify grid-module"
       v-if="hasAccess(REPORTS_APPLICATIONS_READ, REPORTS_APPLICATIONS_READ_MANAGE)">
    <div class="header">
      <h1>Applications</h1>
      <b-notification v-if="['Canceled', 'Failed', 'Renewed'].indexOf(paymentStatus) !== -1"
          :closable=false
          type="alert-block"
          message="Unfortunately, your subscription has expired and wasn't renewed with us.
        Therefore, we are unable to provide you any new details on the submitted
        applications.<br/>
        However, you can still access this information by renewing your subscription with
        Enhancify. Feel free to contact us at Support@Enhancify.com or 813-212-3073"
      />
      <div class="grid-module__filter">
        <div class="modern-form-group sm-modern-form-group">
          <div class="floating-button">
            <b-button
              class="button-right sm-show"
              :disabled="btnExportsDisabled"
              @click="onExportClick"
              type="outlined"
              position="is-right"
              style="min-width: 160px; margin: auto">
              EXPORT
            </b-button>
          </div>
          <div class="modern-input-labeled sm-flex-column"
               v-if="coBrandedContractorPagesOptions.length > 2">
            <label for="coBrandedPageFilter">Co-branded Page</label>
            <ModernSelect
              id="coBrandedPageFilter"
              v-model="coBrandedPageFilter"
              :options="coBrandedContractorPagesOptions"
            />
          </div>
        </div>
        <div class="modern-form-group">
          <div class="modern-input-labeled" v-on:keyup.enter="doSearch">
            <label for="applicantFilter" class="hide-label">Applicant</label>
            <div class="modern-input-controlled">
              <ModernInput id="applicantFilter"
                           type="search"
                           placeholder="Search by contacts"
                           v-model="searchText"/>
              <div class="modern-input-control">
                <button class="modern-button" :disabled="isFormDisabled" @click="doSearch" >
                  <i class="mdi mdi-magnify"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="modern-input-labeled">
            <label for="startedFilter">Started</label>
            <ModernDateSelect
                id="startedFilter"
                ref="modernDateSelect"
                v-model="startedFilter"
                :minDate="startedFilterMin"
                :maxDate="startedFilterMax"
                :options="startedOptions"
            />
          </div>
          <div class="modern-input-labeled">
            <label for="currentStatusFilter" style="white-space: nowrap">Current Status</label>
            <ModernSelect
              id="currentStatusFilter"
              v-model="status"
              :options="statusOptions"
            />
          </div>
          <div class="floating-button">
            <b-button
              class="button-right sm-hide"
              :disabled="btnExportsDisabled"
              @click="onExportClick"
              type="outlined"
              position="is-right"
              style="min-width: 160px; margin: auto">
              EXPORT
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <section class="data-grid">
      <template v-if="!data.length">
        <b-table
            class="modern-table"
            height="100%"
            :columns="columns"
            :loading="loading">
          <template #header="{ column }">
            <div :class="[{'top-sticked': isTopSticky}, column['thclass'],
         'modern-table-header-label']">
              {{ column['label'] }}
              <reports-table-tooltip
                  v-if="help[column['field']]"
                  :content="help[column['field']]" />
            </div>
          </template>

          <template #empty v-if="!loading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
          <span class="icon">
            <i class="fal fa-frown fa-3x"></i>
          </span>
              <p>Nothing here</p>
            </div>
          </template>
        </b-table>
      </template>
      <template v-else>
        <ModernTable
            class="modern-table"
            :loading="loading"
            :per-page="filters.limit"
            :total="total"
            :current-page.sync="filters.page"
            :data="data"
            paginated
            hoverable
            pagination-size="is-medium"
            backend-pagination
            backend-sorting
            :defaultSort="defaultSort"
            @sort="onSort"
        >

          <template #header="{ column }">
            <div :class="[{'top-sticked': isTopSticky}, column['thclass'],
         'modern-table-header-label']"
                 :ref="column['field']">
              {{ column['label'] }}
            </div>
          </template>

          <template #default="{ row }">
            <b-table-column label="Applicant"
                            field="applicantName"
                            width="270"
                            cell-class="report-table__applicant-name-column"
                            header-class="report-table__header
                             report-table__applicant-column"
                            sticky sortable>
              <template #header="{ column }">
                <span class="report-table__header">
                  {{ column['label'] }}
                </span>
              </template>
              <template>
                <div>
                  <div style="font-weight: 500;padding-bottom: 5px">{{ row['applicantName'] }}</div>
                  <div>{{ row['applicantPhone'] }}</div>
                  <div class="email" style="white-space: nowrap; width: fit-content">
                    {{ row['applicantEmail'] }}
                  </div>
                  <div style="cursor: default" v-tooltip="{
                    content: row['applicantLocationAddress'],
                    delay: {show: 500, hide: 100}}">
                    {{ row['applicantLocation'] }}
                  </div>
                </div>
              </template>
            </b-table-column>
            <b-table-column label="App Form Started"
                            field="appFormStarted"
                            width="140"
                            header-class="app-form-started-header"
                            class="report-table__app-form-started-column"
                            style="background: #f2f5f6; text-align: left" sortable>
              <template #header >
                <span class="report-table__header"
                      v-html="'App&nbsp;Form&nbsp;&nbsp;<br />Started'" />
                <reports-table-tooltip :content="help['appFormStarted']" />
              </template>
              <template>
                <reports-table-tooltip  placement="auto"
                                        v-if="windowWidth < 769"
                                        :content="help['appFormStarted']" />
                <span :class="[{
                  'report-table__value_fixed': windowWidth < 769
                }]">
                  <span >{{ row['appFormStarted'] }}</span>
                </span>
              </template>
            </b-table-column>
            <b-table-column label="Self Reported Credit Score"
                            field="selfReportedCreditScore"
                            width="192"
                            header-class="self-reported-credit-score-header"
                            class="report-table__self-reported-credit-score-colum has-text-left"
                            sortable>
              <template #header>
                <span class="report-table__header" v-html="'Self Reported<br/>Credit Score'" />
                <reports-table-tooltip :content="help['selfReportedCreditScore']" />
              </template>
              <template>
                <reports-table-tooltip  placement="auto"
                                        v-if="windowWidth < 769"
                                        :content="help['selfReportedCreditScore']" />
                <span :class="[{
                  'report-table__value_fixed self-reported-credit-score-content': windowWidth < 769
                }]">
                  <span v-if="isEmpty(row, 'selfReportedCreditScore')">–</span>
                  <span v-else>{{ row['selfReportedCreditScore'] }}</span>
                </span>
              </template>
            </b-table-column>
            <b-table-column label="Requested Amount"
                            field="requestedAmount"
                            width="125"
                            header-class="request-amount-header"
                            class="report-table__request-amount-column has-text-right"
                            sortable>
              <template #header>
                <span class="report-table__header" v-html="'Requested&nbsp; <br/>Amount'" />
                <reports-table-tooltip :content="help['requestedAmount']" />
              </template>
              <template>
                <reports-table-tooltip
                    placement="auto"
                    v-if="windowWidth < 769" :content="help['requestedAmount']" />
                <span :class="{'report-table__value_fixed': windowWidth < 769}">
                  <span v-if="isEmpty(row, 'requestedAmount')">–</span>
                  <span v-else> {{ row['requestedAmount'] | currency }} </span>
                </span>
              </template>
            </b-table-column>
            <b-table-column
                label="App Form Completed"
                field="appFormComplete"
                width="181"
                header-class="app-form-completed-header"
                class="report-table__app-form-completed-column has-text-centered"
                sortable>
              <template #header>
                <span class="report-table__header" v-html="'App Form <br /> Completed'" />
                <reports-table-tooltip class="some" :content="help['appFormComplete']" />
              </template>
              <template>
                <reports-table-tooltip
                    placement="auto"
                    v-if="windowWidth < 769" :content="help['appFormComplete']" />
                <span :class="{'report-table__value_fixed': windowWidth < 769}">
                  <span v-if="parseInt(row['appFormComplete'], 10) === 1" class="icon icon-success">
                  <i class="far fa-check-circle fa-lg"></i>
                  </span>
                  <span v-else>–</span>
                </span>
              </template>
            </b-table-column>
            <b-table-column
                label="Received Offers"
                width="290"
                header-class="received-offers-header"
                field="receivedOffers"
                class="report-table__receive-offers-column">
              <template #header="{ column }">
                <span class="report-table__header">
                  {{ column['label'] }}
                </span>
                <reports-table-tooltip
                    placement="left-end"
                    :content="help['receivedOffers']" />
              </template>
              <template>
                <reports-table-tooltip
                    placement="auto"
                    v-if="windowWidth < 769" :content="help['receivedOffers']" />
                <span style="width: 100%" :class="{'report-table__value_fixed': windowWidth < 769}">
                <span v-if="getReceivedOffers(row['receivedOffers']).length !== 0 ">
                  <template v-for="(data, idx) in getReceivedOffers(row['receivedOffers'])">
                    <div :key="idx">
                      <p style="white-space: nowrap"
                         class="table-column-500" v-html="formattedReceivedOffers(idx, data)" />
                      <span v-if="data['selected'] === 1" class="label-offers">SELECTED</span>
                      <span v-if="data['qualified'] === 1" class="label-offers label-offers__blue">
                        QUALIFIED
                      </span>
                    </div>
                  </template>
                </span>
                <span v-else>–</span>
              </span>
              </template>
            </b-table-column>
            <b-table-column
                label="Credit Decision"
                width="190"
                header-class="credit-decision-header"
                class="report-table__credit-decision-column"
                style="text-align: center"
                field="creditDecision"
                sortable>
              <template #header="{ column }">
                <span class="report-table__header"
                    v-html="'Credit&nbsp;<br> Decision'">
                  {{ column['label'] }}
                </span>
                <reports-table-tooltip
                    placement="left-end"
                    :content="help['creditDecision']" />
              </template>
              <template>
                <reports-table-tooltip
                    placement="auto"
                    v-if="windowWidth < 769" :content="help['creditDecision']" />
                <span :class="{'report-table__value_fixed': windowWidth < 769}">
                  <span v-if="isEmpty(row, 'creditDecision')">–</span>
                  <span v-else :class="{
                    'table-cd-approved': row['creditDecision'].toUpperCase() === 'APPROVED',
                    'table-cd-expired': row['creditDecision'].toUpperCase() === 'EXPIRED'
                  }">{{ row['creditDecision'] }}</span>
                </span>
              </template>
            </b-table-column>
            <b-table-column label="Funded"
                            width="180"
                            field="funded"
                            class="report-table__funded-column">
              <template #header="{ column }">
              <span class="report-table__header">
                {{ column['label'] }}
              </span>
                <reports-table-tooltip
                    placement="left-end"
                    :content="help['funded']" />
              </template>
              <template>
                <reports-table-tooltip  placement="auto"
                                        v-if="windowWidth < 769" :content="help['funded']" />
                <span :class="{'report-table__value_fixed': windowWidth < 769}">
                <template v-if="getFunded(row['funded']).length > 0">
                  <p v-for="(data, idx) in getFunded(row['funded'])" :key="idx" class="funded">
                    {{ data }}
                  </p>
                </template>
                <template v-else>
                  –
                </template>
              </span>
              </template>
            </b-table-column>
            <b-table-column label="Funded Date"
                            width="200"
                            field="fundedDate"
                            header-class="funded-date-header"
                            class="report-table__funded-date-column"
                            sortable>
              <template #header>
                <span class="report-table__header" v-html="'Funded <br />Date'" />
                <reports-table-tooltip :content="help['fundedDate']" />
              </template>
              <template>
                <reports-table-tooltip  placement="auto"
                                        v-if="windowWidth < 769" :content="help['fundedDate']" />
                <span :class="{'report-table__value_fixed': windowWidth < 769}">
                <span v-if="!row['fundedDate']">–</span>
                <span v-else>{{ row['fundedDate'] }}</span>
              </span>
              </template>
            </b-table-column>

          </template>

          <template #bottom-left>

            <div class="modern-input-labeled">
              <label class="per-page"
                     style="font-weight: 700; white-space: nowrap">
                Per page
              </label>
              <ModernSelect
                  style="margin-right: 1em"
                  v-model="filters.limit"
                  :options="perPageOptions"
              />
            </div>
          </template>

          <template #empty v-if="!loading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
          <span class="icon">
            <i class="fal fa-frown fa-3x"></i>
          </span>
              <p>Nothing here</p>
            </div>
          </template>
        </ModernTable>
      </template>
    </section>
    <div class="grid-module__footer table-note">
      <p>
        * Not real-time reporting. Enhancify relies on data received from lenders/partners,
        meaning the information will be reported/updated in your Account as soon as we receive it.
      </p>
    </div>
  </div>
  <div v-else>
    <p class="denied-report">
      This report is not available in your Plan. Please contact your manager.
    </p>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ReportsTableTooltip from '@/components/Widgets/ReportsTableTooltip.vue';
import ApplicationsReportHelp from '@/assets/ApplicationsReportHelp';
import { isNull, pick } from 'lodash';
import dayjs from 'dayjs';
import { RANGE } from '@/const/constants';
import ModernTable from '@/components/ModernTable.vue';
import applications from '../api/report_applications';

import ModernSelect from '../components/ModernSelect.vue';
import { getContractorCoBrandedPages } from '../api/contractor';
import ModernDateSelect from '../components/ModernDateSelect.vue';
import ModernInput from '../components/ModernInput.vue';
import { getRange } from '../utils/range';

export default {
  name: 'ReportsApplications',
  components: {
    ModernInput,
    ModernDateSelect,
    ReportsTableTooltip,
    ModernSelect,
    ModernTable,
  },

  props: {
    customStart: {
      type: Date,
      default: null,
    },
    customEnd: {
      type: Date,
      default: null,
    },
    currentPeriod: {
      type: String,
      default: 'Last30Days',
    },
  },

  computed: {
    ...mapState('user', { userId: ({ id }) => id, contractorId: ({ contractor }) => contractor }),
    ...mapState(['contractor']),
    ...mapGetters(['hasAccess']),
    ...mapState({ windowWidth: (state) => state.windowWidth }),
    watchFilters() {
      return [this.status, this.startedFilter, this.coBrandedPageFilter];
    },
    pageOrderParams() {
      return JSON.stringify(pick(this.filters, ['page', 'sortOrder', 'sortField']));
    },
  },

  data() {
    const today = new Date();
    return {
      isTopSticky: false,
      filters: {
        searchText: '',
        showDifferencesOnly: '',
        statusW9: '',
        statusPayment: '',
        tier: '',
        cpActivation: '',
        page: 1,
        limit: 25,
        sortField: 'appFormStarted',
        sortOrder: 'desc',
      },
      columns: [
        {
          field: 'applicantName',
          label: 'Applicant',
          sticky: true,
          sortable: true,
          width: 350,
        },
        {
          field: 'appFormStarted',
          label: 'App Form Started',
          sortable: true,
          width: 180,
        },
        {
          field: 'selfReportedCreditScore',
          label: 'Self Reported Credit Score',
          sortable: true,
          width: 220,
        },
        {
          field: 'requestedAmount',
          label: 'Requested Amount',
          sortable: true,
          width: 200,
        },
        {
          field: 'appFormComplete',
          label: 'App Form Completed',
          sortable: true,
          width: 200,
        },
        {
          field: 'receivedOffers',
          label: 'Received Offers',
          width: 290,
        },
        {
          field: 'creditDecision',
          label: 'Credit Decision',
          sortable: true,
          width: 190,
        },
        {
          field: 'funded',
          label: 'Funded',
          width: 180,
        },
        {
          field: 'fundedDate',
          label: 'Funded Date',
          sortable: true,
          width: 200,
        },
      ],
      resizeObserver: null,
      data: [],
      paymentStatus: null,

      coBrandedPageFilter: null,
      startedFilter: { selectedRange: RANGE.LAST_30_DAYS, ...getRange(RANGE.LAST_30_DAYS) },
      status: '',

      modalActive: false,
      startedFilterMin: null,
      startedFilterMax: new Date(today.getFullYear(), today.getMonth(), today.getDate()),

      startedOptions: [
        { value: RANGE.CONTRACTOR_TIME, label: 'All time' },
        { value: RANGE.LAST_365_DAYS, label: 'Last 365 days' },
        { value: RANGE.LAST_30_DAYS, label: 'Last 30 days' },
        { value: RANGE.LAST_7_DAYS, label: 'Last 7 days' },
        { value: RANGE.LAST_MONTH, label: 'Last month' },
        { value: RANGE.LAST_WEEK, label: 'Last week' },
        { value: RANGE.YESTERDAY, label: 'Yesterday' },
        { value: RANGE.TODAY, label: 'Today' },
        { value: RANGE.CUSTOM, label: 'Custom' },
      ],
      statuses: [
        { value: 'all', text: ' ' },
        { value: 'funded', text: 'Funded' },
        { value: 'credit', text: 'Credit Decision' },
        { value: 'offer', text: 'Offer Selected' },
        { value: 'qualifed', text: 'PreQualified' },
        { value: 'notqualifed', text: 'Not PreQualified' },
        { value: 'started', text: 'App Form Started' },
      ],

      coBrandedContractorPagesOptions: [],
      statusOptions: [
        { value: 'all', label: ' ' },
        { value: 'funded', label: 'Funded' },
        { value: 'credit', label: 'Credit Decision' },
        { value: 'offer', label: 'Offer Selected' },
        { value: 'qualifed', label: 'PreQualified' },
        { value: 'notqualifed', label: 'Not PreQualified' },
        { value: 'started', label: 'App Form Started' },
      ],
      isFormDisabled: false,
      btnExportsDisabled: true,
      btsearch: false,
      total: 0,
      loading: false,
      defaultSort: 'appFormStarted',
      defaultSortDirection: 'desc',
      perPageOptions: [{ value: 10 }, { value: 25 }, { value: 50 }, { value: 100 }],
      error: null,
      showFilter: true,
      searchText: '',
      isDataEmpty: true,

      help: ApplicationsReportHelp,

      REPORTS_APPLICATIONS_READ: { resource: 'reports-applications', code: 'read' },
      REPORTS_APPLICATIONS_READ_MANAGE: { resource: 'reports-applications', code: 'manage_read' },
    };
  },


  mounted() {
    const today = new Date();
    if (this.contractor) {
      this.startedFilterMin = new Date(this.contractor.date);
      this.startedFilterMin.setDate(this.startedFilterMin.getDate() - 1);
      this.startedFilterMax = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    }
  },

  async activated() {
    const today = new Date();
    this.paymentStatus = null;
    if (this.currentPeriod === 'Custom' && this.customStart && this.customEnd) {
      this.status = '';
      this.searchText = '';
      this.startedFilter = {
        selectedRange: 'Custom',
        from: this.customStart,
        to: this.customEnd,
      };
      this.$refs.modernDateSelect.setCustomRange({ from: this.customStart, to: this.customEnd });
    }
    if (this.contractor) {
      this.startedFilterMin = new Date(this.contractor.date);
      this.startedFilterMin.setDate(this.startedFilterMin.getDate() - 1);
      this.startedFilterMax = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    }

    if (this.contractor.id) {
      const { data } = await getContractorCoBrandedPages(this.contractor.id);
      this.coBrandedContractorPagesOptions = [{ value: null, label: 'All' }, ...data];
    }
    this.onSort(this.defaultSort, this.defaultSortDirection);
  },

  watch: {
    watchFilters() {
      this.doSearch();
    },
    userId() {
      this.data = [];
    },
    pageOrderParams() {
      this.loadAsyncData();
    },
    'filters.limit': 'doSearch',
  },

  methods: {
    isEmpty(collection, field) {
      if (Object.prototype.hasOwnProperty.call(collection, field)) {
        return isNull(collection[field]) || collection[field].trim().length === 0;
      }
      return true;
    },

    getFunded(rawValue) {
      try {
        const asJson = JSON.parse(rawValue);
        if (Object.keys(asJson).length < 1) {
          return [];
        }

        return Object.keys(asJson).map((key) => ({
          loan: `• ${this.$options.filters.currency(asJson[key].sum, 2)} (Loans)`,
          direct: `• ${asJson[key].name ?? ''}`,
          creditcard: '• 0% funding',
        }[key]));
      } catch (e) {
        return [];
      }
    },

    getReceivedOffers(rawValue) {
      try {
        return JSON.parse(rawValue);
      } catch (e) {
        return {};
      }
    },

    onExportClick() {
      const requestParameters = this.makeRequestParameters();
      const url = new URL('api/v1/reports-applications-export', document.location.origin);

      Object.entries(requestParameters)
        .forEach(([key, value]) => {
          if (value !== null) {
            url.searchParams.set(key, value);
          }
        });

      const hiddenDownloadLink = document.createElement('a');
      hiddenDownloadLink.href = url.toString();
      hiddenDownloadLink.rel = 'noopener noreferrer';
      hiddenDownloadLink.click();
    },

    makeRequestParameters() {
      return {
        'started[gte]': dayjs(this.startedFilter.from).format('YYYY-MM-DDTHH:mm:ss'),
        'started[lte]': dayjs(this.startedFilter.to).format('YYYY-MM-DDTHH:mm:ss'),
        userId: this.userId,
        page: this.filters.page,
        limit: this.filters.limit,
        order: `${this.filters.sortOrder === 'desc' ? '-' : '+'}${this.filters.sortField}`,
        coBrandedPage: this.coBrandedPageFilter,
        search: this.searchText,
        status: this.status === 'all' ? null : this.status,
      };
    },

    loadAsyncData() {
      //  todo: имхо бесполезная проверка
      if (!this.hasAccess(this.REPORTS_APPLICATIONS_READ, this.REPORTS_APPLICATIONS_READ_MANAGE)) {
        return;
      }

      this.isFormDisabled = true;
      this.btnExportsDisabled = true;
      this.loading = true;

      applications(this.makeRequestParameters()).then(({ data }) => {
        this.data = data.data;
        this.total = data.total;
        this.paymentStatus = data.paymentStatus;
      }).finally(() => {
        this.isFormDisabled = false;
        this.btnExportsDisabled = this.data.length === 0;
        this.loading = false;
      });
    },
    onSort(field, order) {
      this.filters.sortField = field;
      this.filters.sortOrder = order;
      this.loadAsyncData();
    },
    doSearch() {
      this.filters.page = 1;
      this.loadAsyncData();
    },

    /**
     *
     * @param key
     * @param {Object} data
     * @param {number|undefined} data.cnt offers count
     * @param {number|undefined} data.max_amount
     * @param {string|undefined} data.name
     * @returns {string}
     */
    formattedReceivedOffers(key, data) {
      const getMaxAmount = (maxAmount) => (maxAmount !== 0
        ? `–&nbsp;up&nbsp;to&nbsp;${this.$options.filters.currency(maxAmount)}`
        : '');

      switch (key) {
        case 'unsecured_loan': return `•&nbsp;Unsecured&nbsp;Personal&nbsp;Loans <br/> (${data.cnt}&nbsp;offers&nbsp;${getMaxAmount(data.max_amount)})`;
        case 'secured_loan': return `•&nbsp;Secured&nbsp;Personal&nbsp;Loans <br/> (${data.cnt}&nbsp;offers&nbsp;${getMaxAmount(data.max_amount)})`;
        case 'heloc': return `•&nbsp;HELOC <br/>(${data.cnt}&nbsp;offers&nbsp;${getMaxAmount(data.max_amount)})`;
        case 'direct': return `•&nbsp;${data.name}`;
        case 'creditcard': return '•&nbsp;0%&nbsp;funding';
        case 'no_offers': return '•&nbsp;No&nbsp;Offers';
        default: return '-';
      }
    },
  },
};

</script>

<style lang="stylus">
.report-table__applicant-column
  min-width 190px
.report-table__funded-column
  min-width 142px
.report-table__funded-date-column
  min-width 60px
.report-table__self-reported-credit-score-colum
  min-width 170px
.report-table__receive-offers-column
  min-width 290px
.report-column__credit-decision-column
  min-width 150px
.report-table__app-form-column
  min-width 155px
</style>

<style scoped lang="stylus">
@require "../assets/style/modern/variables.styl";
// todo: костыли в стилях, перенести в глобальные стили
#coBrandedPageFilter
  max-width 920px

.sm-show
  display none
.sm-flex-column
  min-width 100%
.grid-module__filter
  padding 10px 0 20px

.modern-table-header-label
  margin-right 0

@media (max-width: $breakpoints.md)
  .modern-form-group > *
    margin-right 0
  .sm-hide
    display none
  .sm-show
    display block

@media (max-width: $breakpoints.sm)
  .b-table
    margin-top 0
  .sm-flex-column
    flex-direction column
    margin-bottom 0
    & > label
      width 100%
      padding-bottom $input-padding-y
  .report-table__receive-offers-column
    min-width 200px

@media (max-width: $breakpoints.lg)
  .sm-modern-form-group
    margin-bottom 0
    padding-bottom 0
  .hide-label
    display none
  .report-table__applicant-column
    width 180px

.modern-input-labeled
  label
    white-space nowrap

.floating-button
  margin-left auto
  margin-right 0

.far.fa-check-circle.fa-lg
  color #17b548
.report-table
  &__column_fix
    justify-content flex-start !important
  &__header
    color white
    font-weight 500
    font-size 18px
    @media (max-width: $breakpoints.lg)
      font-size 16px
    position relative
    .some
      position absolute
  &__icon
    position absolute
    color #525F66
    font-size 18px
    margin-left 3px
  &__value_fixed
    margin-left auto
    &.self-reported-credit-score-content
      text-align right
      @media (max-width: $breakpoints.md)
        width 100%
  @media (max-width: $breakpoints.md)
    &__applicant-name-column
      &::before
        white-space nowrap
      .report-table__value_fixed
        margin-left 10px
  &__applicant-name-column
  &__app-form-started-column
  &__self-reported-credit-score-colum
  &__request-amount-column
  &__app-form-completed-column
  &__receive-offers-column
  &__credit-decision-column
  &__funded-column
  &__funded-date-column
    @media (min-width: $breakpoints.md) and (max-width: $breakpoints.lg)
      font-size 16px!important
    @media (max-width: $breakpoints.md)
      font-size 14px!important
.modern-table >>>
  .self-reported-credit-score-header,
  .app-form-started-header,
  .request-amount-header,
  .app-form-completed-header,
  .received-offers-header,
  .credit-decision-header,
  .funded-date-header
    line-height 1

.label-offers
  font-size 14px
  border 1px #1aab48 solid
  color #1aab48
  padding 3px
  border-radius 4px
  font-weight 400
  &__blue
    border 1px #3273dc solid
    color #3273dc

.funded
  color rgb(28, 65, 140)
  font-weight 600

.field.date-filter.fullwidth.is-grouped
  margin-bottom 0
@media (max-width: $breakpoints.md)
  .email
    margin-left auto

</style>
